import React from 'react';
import { AiPageContextSync } from '@/components/ai-agent/AiPageContextSync';
import { Button, Card, Col, Descriptions, Row, Space, Spin, Table, Tabs, Tag, Timeline, Typography, message, Alert } from 'antd';
import { ArrowLeftOutlined, FileTextOutlined, LinkOutlined } from '@ant-design/icons';
import { useAuthStore } from '@/store/authStore';
import { useNavigate, useParams, Link } from 'react-router-dom';
import customerLifecycleService from '@/services/customerLifecycle.service';
import { useQuery } from '@tanstack/react-query';
import { useCurrency } from '@/contexts/CurrencyContext';
import CustomerInteractionsPanel from '@/components/crm/activities/CustomerInteractionsPanel';
import { formatDate } from '@/utils/format/date';

const { Title, Text } = Typography;

const JOURNEY_PATH: Record<string, string> = {
  lead: '/growth/leads',
  opportunity: '/growth/opportunities',
  quotation: '/growth/quotations',
  sales_order: '/growth/orders',
  delivery: '/growth/delivery-notes',
  invoice: '/growth/invoices',
};

const Customer360Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { formatCurrency } = useCurrency();
  const user = useAuthStore((s) => s.user);
  const canViewStatement = Boolean(
    user?.is_super_admin || user?.permissions?.some((p) => p === 'finance.reports.view' || p === 'finance.view')
  );

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['customer-360', id],
    queryFn: () => customerLifecycleService.get360(id!),
    enabled: !!id,
  });

  const issuePortal = async () => {
    if (!id) return;
    const res = await customerLifecycleService.issuePortalToken(id);
    const url = `${window.location.origin}${res?.portal_path}`;
    await navigator.clipboard.writeText(url);
    message.success('Portal link copied to clipboard');
    refetch();
  };

  if (!id) return null;

  const customerLabel = String(
    data?.customer?.customer_name ?? data?.customer?.name ?? data?.customer?.customer_code ?? ''
  );

  return (
    <div style={{ padding: 24 }}>
      {id ? <AiPageContextSync module="sales" docType="Customer" docId={id} /> : null}
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/growth/customers')}>
          Customers
        </Button>
        <Button icon={<LinkOutlined />} onClick={issuePortal} disabled={!data?.customer}>
          Copy portal link
        </Button>
        {canViewStatement && (
          <Button icon={<FileTextOutlined />} onClick={() => navigate(`/fin/reports/customer-statement?customer_id=${id}`)}>
            Statement of account
          </Button>
        )}
      </Space>
      {isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="Failed to load Customer 360"
          description={(error as Error)?.message ?? 'Please try again.'}
        />
      ) : null}
      <Spin spinning={isLoading}>
        <Title level={3}>Customer 360 — {customerLabel}</Title>
        <Tabs
          items={[
            {
              key: 'journey',
              label: 'Journey Timeline',
              children: (
                <Timeline
                  items={(data?.journey_timeline ?? []).map((t) => ({
                    color: t.type === 'activity' ? 'green' : 'blue',
                    children: (
                      <div>
                        <Tag color={t.type === 'activity' ? 'geekblue' : undefined}>
                          {t.type === 'activity' ? t.label : t.type}
                        </Tag>{' '}
                        {formatDate(t.date)}{' '}
                        {JOURNEY_PATH[t.type] ? (
                          <Link to={`${JOURNEY_PATH[t.type]}/${t.id}`}>{t.title}</Link>
                        ) : (
                          <Text>{t.title}</Text>
                        )}
                        <div>
                          <Text type="secondary">{t.status}</Text>
                        </div>
                      </div>
                    ),
                  }))}
                />
              ),
            },
            {
              key: 'interactions',
              label: 'Interactions',
              children: <CustomerInteractionsPanel customerId={id} summary={data?.interactions} />,
            },
            {
              key: 'commercial',
              label: 'Commercial',
              children: (
                <Row gutter={16}>
                  <Col span={12}>
                    <Card title="Opportunities" size="small">
                      <Table size="small" rowKey="id" pagination={false} dataSource={(data?.presale as { opportunities?: unknown[] })?.opportunities ?? []} columns={[
                        { title: '#', dataIndex: 'opportunity_number' },
                        { title: 'Name', dataIndex: 'opportunity_name' },
                        { title: 'Stage', dataIndex: 'stage' },
                      ]} />
                    </Card>
                  </Col>
                  <Col span={12}>
                    <Card title="Quotations" size="small">
                      <Table size="small" rowKey="id" pagination={false} dataSource={(data?.presale as { quotations?: unknown[] })?.quotations ?? []} columns={[
                        { title: '#', dataIndex: 'quotation_number' },
                        { title: 'Status', dataIndex: 'status' },
                        { title: 'Value', dataIndex: 'grand_total', render: (v: number) => formatCurrency(v) },
                      ]} />
                    </Card>
                  </Col>
                </Row>
              ),
            },
            {
              key: 'financial',
              label: 'Financial',
              children: (
                <Card>
                  <Descriptions column={2}>
                    <Descriptions.Item label="Open AR (est.)">
                      {formatCurrency(Number((data?.financial as { open_ar_estimate?: number })?.open_ar_estimate ?? 0))}
                    </Descriptions.Item>
                  </Descriptions>
                  <Table size="small" rowKey="id" dataSource={(data?.financial as { invoices?: unknown[] })?.invoices ?? []} columns={[
                    { title: 'Invoice', dataIndex: 'invoice_number' },
                    { title: 'Status', dataIndex: 'invoice_status' },
                    { title: 'Payment', dataIndex: 'payment_status' },
                    { title: 'Amount', dataIndex: 'grand_total', render: (v: number) => formatCurrency(v) },
                  ]} />
                </Card>
              ),
            },
          ]}
        />
      </Spin>
    </div>
  );
};

export default Customer360Page;
